import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon, ActivityIcon } from 'lucide-react';
import { useApplyModal } from './ApplyModalContext';
import { Button } from './Button';
import { submitApplication } from '../utils/googleSheets';
interface FormState {
  name: string;
  contact: string;
  social: string;
  fit: string;
}
const initialForm: FormState = {
  name: '',
  contact: '',
  social: '',
  fit: ''
};
export function ApplyModal() {
  const {
    isOpen,
    close
  } = useApplyModal();
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  // Close on Escape + lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [isOpen, close]);
  // Reset form shortly after closing
  useEffect(() => {
    if (isOpen) return;
    const t = setTimeout(() => {
      setForm(initialForm);
      setSubmitted(false);
      setError(null);
      setSubmitting(false);
    }, 250);
    return () => clearTimeout(t);
  }, [isOpen]);
  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitApplication(form);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong submitting your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const fieldClasses = 'w-full rounded-2xl bg-navy-950 border border-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-electric/50 focus:ring-1 focus:ring-electric/30 transition-colors';
  return <AnimatePresence data-id="element-3">
      {isOpen && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm" onClick={close} aria-hidden="true" data-id="element-5" />

          {/* Dialog */}
          <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="apply-modal-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-navy-900/95 backdrop-blur-xl border border-white/10 shadow-2xl box-glow" data-id="element-6">
            {/* Brand accent glow (matches hero) */}
            <div className="absolute -top-20 -right-16 w-56 h-56 bg-electric/10 rounded-full blur-[80px] pointer-events-none" data-id="element-7" />
            <div className="absolute -bottom-20 -left-16 w-56 h-56 bg-mint/10 rounded-full blur-[80px] pointer-events-none" data-id="element-8" />

            <div className="relative p-6 sm:p-8" data-id="element-9">
              <button type="button" onClick={close} aria-label="Close" className="absolute top-5 right-5 text-slate-400 hover:text-white transition-colors p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/30" data-id="element-10">
                <XIcon className="w-5 h-5" data-id="element-11" />
              </button>

              {submitted ? <div className="py-8 text-center" data-id="element-12">
                  <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-electric to-mint flex items-center justify-center mb-5" data-id="element-13">
                    <CheckIcon className="w-7 h-7 text-navy-950" data-id="element-14" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight text-white mb-2" data-id="element-15">
                    Application received.
                  </h2>
                  <p className="text-slate-400 text-sm max-w-sm mx-auto mb-8" data-id="element-16">
                    Thanks for applying to the Creator Lab. We review every
                    application and will reach out if it is a fit.
                  </p>
                  <Button variant="primary" onClick={close} className="px-6 py-3" data-id="element-17">
                    Done
                  </Button>
                </div> : <>
                  {/* Brand lockup */}
                  <div className="flex items-center gap-2 mb-6" data-id="element-18">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-electric to-mint flex items-center justify-center" data-id="element-19">
                      <ActivityIcon className="w-5 h-5 text-navy-950" data-id="element-20" />
                    </div>
                    <span className="text-white font-semibold tracking-tight text-lg" data-id="element-21">
                      Pulsewave
                    </span>
                  </div>

                  <h2 id="apply-modal-title" className="text-2xl font-semibold tracking-tight text-white mb-2 pr-8" data-id="element-22">
                    Apply to the Creator Lab
                  </h2>
                  <p className="text-sm text-slate-400 mb-8" data-id="element-23">
                    4 weeks • 5 creators • $2,000 stipend • Fully remote
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-24">
                    <div data-id="element-25">
                      <label htmlFor="apply-name" className="block text-sm font-medium text-white mb-2" data-id="element-26">
                        Name <span className="text-mint" data-id="element-27">*</span>
                      </label>
                      <input id="apply-name" ref={firstFieldRef} type="text" required value={form.name} onChange={handleChange('name')} placeholder="Your name" className={fieldClasses} data-id="element-28" />
                    </div>

                    <div data-id="element-29">
                      <label htmlFor="apply-contact" className="block text-sm font-medium text-white mb-2" data-id="element-30">
                        Email or phone <span className="text-mint" data-id="element-31">*</span>
                      </label>
                      <input id="apply-contact" type="text" required value={form.contact} onChange={handleChange('contact')} placeholder="email@domain.com or (555) 555-5555" className={fieldClasses} data-id="element-32" />
                    </div>

                    <div data-id="element-33">
                      <label htmlFor="apply-social" className="block text-sm font-medium text-white mb-2" data-id="element-34">
                        Social handles / links{' '}
                        <span className="text-mint" data-id="element-35">*</span>
                      </label>
                      <input id="apply-social" type="text" required value={form.social} onChange={handleChange('social')} placeholder="@handle or profile URLs" className={fieldClasses} data-id="element-36" />
                    </div>

                    <div data-id="element-37">
                      <label htmlFor="apply-fit" className="block text-sm font-medium text-white mb-2" data-id="element-38">
                        Why you're a fit{' '}
                        <span className="text-slate-500 font-normal" data-id="element-39">
                          (optional)
                        </span>
                      </label>
                      <textarea id="apply-fit" rows={4} value={form.fit} onChange={handleChange('fit')} placeholder="Tell us about your audience and why you'd be a great fit for the Creator Lab..." className={`${fieldClasses} resize-none`} data-id="element-40" />
                    </div>

                    {error && <p role="alert" className="text-sm text-red-400 -mt-1" data-id="element-41">
                        {error}
                      </p>}

                    <Button type="submit" variant="primary" disabled={submitting} className="w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-42">
                      {submitting ? 'Submitting…' : 'Submit Application'}
                    </Button>
                  </form>
                </>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}